import React, { useEffect, useState, useCallback, useRef } from "react";
import toast from "react-hot-toast";
import CreateCampaignModal from "../shared/components/CreateCampaignModal";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SummaryButton from "../shared/archive/SummaryButton";
import { BsListNested } from "react-icons/bs";
import CampaignSidebar from "../shared/components/qa/CampaignSidebar";
import { getCampaigns, getCampaignDetails, getSessionDetails, updateSummary, updateTranscript, updateCampaignTitle, createCampaign, getCampaignSummaries, updateCampaignSummary } from "../../services/api";
import { FiEdit2 } from "react-icons/fi";
import API_BASE_URL from '../../config';
import { VscCloudDownload } from "react-icons/vsc";

const SUMMARY_TYPES = {
  BRIEF: "briefSummary",
  MODERATE: "moderateSummary",
  DETAILED: "detailedSummary",
  FULL: "fullTranscript",
};

const Archive = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialSelectionMade = useRef(false);
  const prevSessionRef = useRef(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [campaignDetails, setCampaignDetails] = useState({});
  const [summaryType, setSummaryType] = useState(SUMMARY_TYPES.BRIEF);
  const [summaryContent, setSummaryContent] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isListModalOpen, setIsListModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState("");
  const [isEditingCampaignName, setIsEditingCampaignName] = useState(false);
  const [editCampaignName, setEditCampaignName] = useState("");
  const [selectedSessionTitle, setSelectedSessionTitle] = useState('');
  const [campaignTitle, setCampaignTitle] = useState('');
  const [campaignSummaries, setCampaignSummaries] = useState({});

  const showError = (message) => toast.error(message);
  const showLoading = () => toast.loading("Loading...");
  const dismissLoading = (toastId) => toast.dismiss(toastId);

  const updateSummaryContent = useCallback(async (type, details = campaignDetails) => {
    if (!selectedSession && !details) return;

    try {
      if (selectedSession) {
        const data = await getSessionDetails(selectedSession);
        switch (type) {
          case SUMMARY_TYPES.BRIEF:
            setSummaryContent(data.briefSummary);
            break;
          case SUMMARY_TYPES.MODERATE:
            setSummaryContent(data.moderateSummary);
            break;
          case SUMMARY_TYPES.DETAILED:
            setSummaryContent(data.detailedSummary);
            break;
          case SUMMARY_TYPES.FULL:
            setSummaryContent(data.fullTranscript);
            break;
          default:
            setSummaryContent(data.fullTranscript);
        }
      } else if (selectedCampaign) {
        // If a campaign is selected but no session, show campaign summaries
        if (campaignSummaries[selectedCampaign]) {
          const campaignSummary = campaignSummaries[selectedCampaign];
          switch (type) {
            case SUMMARY_TYPES.BRIEF:
              setSummaryContent(campaignSummary.briefSummary || `No brief summary available for this campaign.`);
              break;
            case SUMMARY_TYPES.MODERATE:
              setSummaryContent(campaignSummary.moderateSummary || `No moderate summary available for this campaign.`);
              break;
            case SUMMARY_TYPES.DETAILED:
              setSummaryContent(campaignSummary.detailedSummary || `No detailed summary available for this campaign.`);
              break;
            case SUMMARY_TYPES.FULL:
              setSummaryContent(campaignSummary.fullTranscript || `No full transcript available for this campaign.`);
              break;
            default:
              setSummaryContent(campaignSummary.briefSummary || `No brief summary available for this campaign.`);
          }
        } else {
          // If we don't have the campaign summaries yet, show a loading message
          const campaign = campaigns.find(c => c.id === selectedCampaign);
          const campaignName = campaign ? campaign.title : "the campaign";
          setSummaryContent(`Loading summaries for ${campaignName}...`);
        }
      } else if (details) {
        setSummaryContent(details[type]);
      }
    } catch (err) {
      showError(err.message);
    }
  }, [selectedSession, campaignDetails, selectedCampaign, campaigns, campaignSummaries]);

  const fetchAllCampaignDetails = async (campaigns) => {
    const details = {};
    for (const campaign of campaigns) {
      try {
        if (!campaign || !campaign.id) continue;
        
        const data = await getCampaignDetails(campaign.id);
        
        // Ensure the data has a transcripts array
        if (!data.transcripts) {
          data.transcripts = [];
        }
        
        details[campaign.id] = data;
      } catch (err) {
        showError(`Failed to fetch details for campaign ${campaign.title}`);
      }
    }
    console.log('All campaign details fetched:', details);
    setCampaignDetails(details);
  };

  const refreshCampaigns = async () => {
    try {
      const data = await getCampaigns();
      if (data && data.campaigns) {
        setCampaigns(data.campaigns);
        await fetchAllCampaignDetails(data.campaigns);
      }
    } catch (error) {
      showError("Failed to refresh campaigns");
    }
  };

  const handleSessionOperation = async (operation, ...args) => {
    const toastId = showLoading();
    try {
      await operation(...args);
      await refreshCampaigns();
    } catch (error) {
      showError(error.message);
    } finally {
      dismissLoading(toastId);
    }
  };

  useEffect(() => {
    const fetchCampaigns = async () => {
      const toastId = showLoading();
      try {
        console.log('Fetching campaigns...');
        const data = await getCampaigns();
        console.log('Fetched data:', data);
        if (data && data.campaigns) {
          setCampaigns(data.campaigns);
          await fetchAllCampaignDetails(data.campaigns);
          
          // Don't select any campaign here
          // Let the CampaignSidebar handle the initial selection based on the sorted list
        } else {
          console.error('No campaigns found in response');
          throw new Error('No campaigns found');
        }
      } catch (error) {
        console.error('Error fetching campaigns:', error);
        showError("Failed to fetch campaigns");
      } finally {
        dismissLoading(toastId);
      }
    };

    fetchCampaigns();
  }, []);

  useEffect(() => {
    const fetchCampaignDetails = async () => {
      setIsListModalOpen(false);
      if (!selectedCampaign) return;

      const toastId = showLoading();
      try {
        console.log('Fetching details for campaign:', selectedCampaign);
        const data = await getCampaignDetails(selectedCampaign);
        console.log('Fetched campaign details:', data);
        
        // Ensure the data has a transcripts array
        if (!data.transcripts) {
          data.transcripts = [];
        }
        
        // Update the campaign details
        setCampaignDetails(prevDetails => ({
          ...prevDetails,
          [selectedCampaign]: data
        }));
        
        if (
          selectedSession &&
          !data.transcripts?.some((s) => s.id === selectedSession)
        ) {
          setSelectedSession(null);
        }
        updateSummaryContent(summaryType, data);
      } catch (err) {
        showError(err.message);
      } finally {
        dismissLoading(toastId);
      }
    };

    fetchCampaignDetails();
  }, [selectedCampaign]);

  // Add a debug log to see what's happening with the campaign details
  useEffect(() => {
    console.log('Campaign details updated:', campaignDetails);
    console.log('Selected campaign:', selectedCampaign);
    if (selectedCampaign && campaignDetails[selectedCampaign]) {
      console.log('Selected campaign transcripts:', campaignDetails[selectedCampaign].transcripts);
    }
  }, [campaignDetails, selectedCampaign]);

  useEffect(() => setIsListModalOpen(false), [selectedSession]);

  // Effect to update summary content when selectedSession or summaryType changes
  useEffect(() => {
    if (selectedSession) {
      console.log(`Archive - Updating summary content for session ${selectedSession} with type ${summaryType}`);
      updateSummaryContent(summaryType);
      
      // Update the previous session reference
      prevSessionRef.current = selectedSession;
    } else if (selectedCampaign) {
      // When no session is selected but a campaign is selected
      console.log(`Archive - No session selected, updating summary with type ${summaryType}`);
      updateSummaryContent(summaryType);
      
      // Reset the previous session reference when no session is selected
      prevSessionRef.current = null;
    }
  }, [selectedSession, summaryType, selectedCampaign, updateSummaryContent]);

  // Effect to reset summary type to BRIEF when a new session is selected
  useEffect(() => {
    if (selectedSession && prevSessionRef.current !== selectedSession) {
      console.log(`Archive - New session selected (${selectedSession}), resetting to BRIEF summary`);
      setSummaryType(SUMMARY_TYPES.BRIEF);
    }
  }, [selectedSession]);

  useEffect(() => {
    if (campaigns.length > 0 && !selectedSession) {
      // Don't set selectedCampaign here as it's now handled by CampaignSidebar
      // The CampaignSidebar will select the first campaign in the sorted list
    }
  }, [campaigns]);

  useEffect(() => {
    // Only proceed if campaigns are loaded
    if (campaigns.length === 0) return;

    const params = new URLSearchParams(location.search);
    const transcriptId = params.get('transcript_id');
    const campaignId = params.get('campaign_id');
    
    console.log('URL parameters check - campaigns loaded:', campaigns.length);
    console.log('URL parameters:', { transcriptId, campaignId });
    
    // Set initialSelectionMade to true immediately if we have URL parameters
    // This prevents the CampaignSidebar from auto-selecting the first campaign
    if (transcriptId || campaignId) {
      console.log('Setting initialSelectionMade to true because URL parameters are present');
      initialSelectionMade.current = true;
    }
    
    // Handle transcript_id parameter
    if (transcriptId) {
      setSelectedSession(transcriptId);
      console.log('Selected session set from URL:', transcriptId);
      
      // Find the campaign that contains this session
      const findCampaignForSession = () => {
        for (const campaign of campaigns) {
          if (!campaign || !campaign.id) continue;
          
          const campaignDetail = campaignDetails[campaign.id];
          if (!campaignDetail || !Array.isArray(campaignDetail.transcripts)) continue;
          
          const sessionExists = campaignDetail.transcripts.some(
            transcript => transcript && transcript.id === transcriptId
          );
          
          if (sessionExists) {
            console.log(`Found campaign ${campaign.id} for session ${transcriptId}`);
            setSelectedCampaign(campaign.id);
            return;
          }
        }
        console.log(`Could not find campaign for session ${transcriptId}`);
      };
      
      // Only try to find the campaign if we have campaign details
      if (Object.keys(campaignDetails).length > 0) {
        findCampaignForSession();
      }
    } 
    // Handle campaign_id parameter
    else if (campaignId) {
      console.log('Selected campaign set from URL:', campaignId, 'Type:', typeof campaignId);
      
      // Check if the campaign exists in our list
      // Convert both IDs to strings for comparison to ensure type consistency
      const campaignExists = campaigns.some(campaign => {
        if (!campaign || !campaign.id) return false;
        console.log('Comparing campaign ID:', campaign.id, 'Type:', typeof campaign.id);
        return String(campaign.id) === String(campaignId);
      });
      
      if (campaignExists) {
        // Find the matching campaign to get its ID with the correct type
        const matchingCampaign = campaigns.find(campaign => 
          campaign && campaign.id && String(campaign.id) === String(campaignId)
        );
        
        if (matchingCampaign) {
          // Use the ID directly from the campaigns array to ensure correct type
          setSelectedCampaign(matchingCampaign.id);
          console.log(`Setting selected campaign to ${matchingCampaign.id} (${typeof matchingCampaign.id}) from URL parameter`);
        } else {
          // Fallback to the URL parameter if for some reason we can't find the matching campaign
          setSelectedCampaign(campaignId);
          console.log(`Fallback: Setting selected campaign to ${campaignId} from URL parameter`);
        }
        
        // Reset any selected session when directly selecting a campaign
        setSelectedSession(null);
      } else {
        console.log(`Campaign ${campaignId} from URL parameter not found in available campaigns`);
      }
    }
  }, [location, campaigns, campaignDetails]);

  useEffect(() => {
    const fetchCampaignAndSummary = async () => {
      if (selectedSession) {
        try {
          const response = await fetch(`${API_BASE_URL}/api/get_campaign_and_summary/${selectedSession}/`, {
            credentials: 'include',
          });
          if (!response.ok) {
            throw new Error('Failed to fetch campaign and session details');
          }
          const data = await response.json();
          setCampaignTitle(data.campaign_name);
          setSelectedSessionTitle(data.summary_very_short);
        } catch (error) {
          console.error('Error fetching campaign and session details:', error);
        }
      }
    };

    fetchCampaignAndSummary();
  }, [selectedSession]);

  useEffect(() => {
    const fetchCampaignSummaries = async () => {
      if (!selectedCampaign) return;
      
      try {
        console.log('Fetching campaign summaries for campaign:', selectedCampaign);
        const data = await getCampaignSummaries(selectedCampaign);
        console.log('Fetched campaign summaries:', data);
        
        setCampaignSummaries(prev => ({
          ...prev,
          [selectedCampaign]: data
        }));
        
        // If no session is selected, update the summary content with the campaign summaries
        if (!selectedSession) {
          updateSummaryContent(summaryType);
        }
      } catch (err) {
        console.error('Error fetching campaign summaries:', err);
        showError(`Failed to fetch campaign summaries: ${err.message}`);
      }
    };

    fetchCampaignSummaries();
  }, [selectedCampaign, selectedSession, summaryType]);

  const handleSummaryTypeChange = async (type) => {
    setIsEditing(false);
    setSummaryType(type);
    console.log('Summary type changed to:', type);
    await updateSummaryContent(type);
  };

  const handleSessionSelect = async (sessionId) => {
    setIsListModalOpen(false);
    
    // Check if this is a different session than the currently selected one
    const isNewSession = sessionId !== selectedSession;
    
    // Update the selected session
    setSelectedSession(sessionId);
    
    // Only reset editing state if selecting a new session
    if (isNewSession) {
      setIsEditing(false);
      // We don't need to set the summary type here as the useEffect will handle it
      // based on the prevSessionRef comparison
    }
    
    // Update URL with the selected session
    const params = new URLSearchParams(location.search);
    params.set('transcript_id', sessionId);
    if (selectedCampaign) {
      params.set('campaign_id', selectedCampaign);
    }
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    
    console.log('Archive - Updated URL with session:', sessionId);
    
    // We don't need to call updateSummaryContent here as the useEffect will handle it
  };

  // Add a function to handle campaign selection that updates the URL
  const handleCampaignSelect = (campaignId) => {
    setSelectedCampaign(campaignId);
    setSelectedSession(null); // Clear selected session when changing campaigns
    
    // Update URL with the selected campaign
    const params = new URLSearchParams(location.search);
    params.set('campaign_id', campaignId);
    params.delete('transcript_id'); // Remove transcript_id when changing campaigns
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    
    console.log('Archive - Updated URL with campaign:', campaignId);
  };

  const handleCreateCampaign = async (formData) => {
    const toastId = showLoading();
    try {
      const result = await createCampaign(formData.name);
      if (result.success) {
        // Refresh the campaigns list
        await refreshCampaigns();
        toast.success("Campaign created successfully");
        setIsModalOpen(false);
      }
    } catch (err) {
      showError(err.message);
    } finally {
      dismissLoading(toastId);
    }
  };

  const handleEditClick = () => {
    // Set the edit content based on the current summary type
    if (selectedSession) {
      // For session summaries
      setEditContent(summaryContent);
    } else if (selectedCampaign && campaignSummaries[selectedCampaign]) {
      // For campaign summaries
      const campaignSummary = campaignSummaries[selectedCampaign];
      switch (summaryType) {
        case SUMMARY_TYPES.BRIEF:
          setEditContent(campaignSummary.briefSummary || '');
          break;
        case SUMMARY_TYPES.MODERATE:
          setEditContent(campaignSummary.moderateSummary || '');
          break;
        case SUMMARY_TYPES.DETAILED:
          setEditContent(campaignSummary.detailedSummary || '');
          break;
        case SUMMARY_TYPES.FULL:
          setEditContent(campaignSummary.fullTranscript || '');
          break;
        default:
          setEditContent(campaignSummary.briefSummary || '');
      }
    }
    setIsEditing(true);
    setIsEditingCampaignName(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditContent('');
  };

  const handleSaveEdit = async () => {
    const toastId = showLoading();
    try {
      if (selectedSession) {
        // Handle session summary updates (existing code)
        switch (summaryType) {
          case SUMMARY_TYPES.BRIEF:
            await updateSummary(selectedSession, "briefSummary", editContent);
            break;
          case SUMMARY_TYPES.MODERATE:
            await updateSummary(selectedSession, "moderateSummary", editContent);
            break;
          case SUMMARY_TYPES.DETAILED:
            await updateSummary(selectedSession, "detailedSummary", editContent);
            break;
          case SUMMARY_TYPES.FULL:
            await updateTranscript(selectedSession, editContent);
            break;
          default:
            break;
        }
      } else if (selectedCampaign) {
        // Handle campaign summary updates
        await updateCampaignSummary(selectedCampaign, summaryType, editContent);
        
        // Update the local state
        setCampaignSummaries(prev => ({
          ...prev,
          [selectedCampaign]: {
            ...prev[selectedCampaign],
            [summaryType]: editContent
          }
        }));
      }

      setIsEditing(false);
      updateSummaryContent(summaryType);
      toast.success("Content updated successfully");
    } catch (err) {
      showError(err.message);
    } finally {
      dismissLoading(toastId);
    }
  };

  const handleEditCampaignNameClick = (campaignName) => {
    setIsEditingCampaignName(true);
    setIsEditing(false);
    setEditCampaignName(campaignName);
  };

  const handleSaveCampaignName = async (campaignId) => {
    try {
      await updateCampaignTitle(campaignId, editCampaignName);
      setIsEditingCampaignName(false);
    } catch (error) {
      showError("Failed to save campaign name");
    }
  };

  const handleDownloadSummary = () => {
    if (!selectedSession && !selectedCampaign) return;
    
    let content = '';
    let filename = '';
    
    if (selectedSession) {
      // Handle session summary download
      content = summaryContent;
      const sessionTitle = campaignDetails[selectedCampaign]?.transcripts?.find(t => t.id === selectedSession)?.title || 'session';
      const summaryTypeText = summaryType === SUMMARY_TYPES.BRIEF ? 'brief' : 
                             summaryType === SUMMARY_TYPES.MODERATE ? 'moderate' : 
                             summaryType === SUMMARY_TYPES.DETAILED ? 'detailed' : 'full';
      filename = `${sessionTitle.replace(/\s+/g, '_')}_${summaryTypeText}.txt`;
    } else if (selectedCampaign && campaignSummaries[selectedCampaign]) {
      // Handle campaign summary download
      const campaignSummary = campaignSummaries[selectedCampaign];
      switch (summaryType) {
        case SUMMARY_TYPES.BRIEF:
          content = campaignSummary.briefSummary || '';
          break;
        case SUMMARY_TYPES.MODERATE:
          content = campaignSummary.moderateSummary || '';
          break;
        case SUMMARY_TYPES.DETAILED:
          content = campaignSummary.detailedSummary || '';
          break;
        case SUMMARY_TYPES.FULL:
          content = campaignSummary.fullTranscript || '';
          break;
        default:
          content = campaignSummary.briefSummary || '';
      }
      
      const campaignName = campaigns.find(c => c.id === selectedCampaign)?.title || 'campaign';
      const summaryTypeText = summaryType === SUMMARY_TYPES.BRIEF ? 'brief' : 
                             summaryType === SUMMARY_TYPES.MODERATE ? 'moderate' : 
                             summaryType === SUMMARY_TYPES.DETAILED ? 'detailed' : 'full';
      filename = `${campaignName.replace(/\s+/g, '_')}_${summaryTypeText}.txt`;
    }
    
    // Strip HTML tags
    content = content.replace(/<[^>]*>?/gm, '');
    
    // Create a blob and download
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  console.log('Selected session passed to CampaignSidebar:', selectedSession);

  return (
    <>
      <button
        onClick={() => setIsListModalOpen(true)}
        className="bg-gray-100 mx-6 mt-1 sticky top-0 md:hidden text-sm mx-auto flex items-center justify-center bg-primary hover:bg-primary-light space-x-2 text-white rounded-lg p-1">
        <BsListNested size={20} />
        <span>View Campaigns</span>
      </button>

      <main className="min-h-[calc(100vh-88px)] md:h-[calc(100vh-56px)] md:flex flex-col md:flex-row gap-8 bg-gray-100 p-6 overflow-hidden">
        {/* Left Sidebar */}
        <CampaignSidebar
          origin="archive"
          campaigns={campaigns}
          selectedCampaign={selectedCampaign}
          setSelectedCampaign={handleCampaignSelect}
          campaignDetails={campaignDetails}
          setSelectedSession={handleSessionSelect}
          selectedSession={selectedSession}
          isOpen={isListModalOpen}
          onClose={() => setIsListModalOpen(false)}
          setIsModalOpen={setIsModalOpen}
          handleSessionSelect={handleSessionSelect}
          onSessionOperation={handleSessionOperation}
          initialSelectionMade={initialSelectionMade}
        />

        {/* Main Content Area */}
        <div className="md:w-3/4 overflow-y-auto">
          <div className="max-w-4xl mx-auto">
            {/* Summary View Options */}
            <h2 className="text-xl font-semibold mb-4 text-center text-sky-900">
              {selectedSession 
                ? (campaignTitle && selectedSessionTitle ? `${campaignTitle}: ${selectedSessionTitle}` : 'Loading session...') 
                : selectedCampaign 
                  ? `Campaign: ${campaigns.find(c => c.id === selectedCampaign)?.title || 'Loading...'}`
                  : 'Select a Campaign and Session to view summaries'}
            </h2>

            <div className="flex flex-wrap gap-2 justify-center md:space-x-4 mb-6">
              <SummaryButton
                type={SUMMARY_TYPES.BRIEF}
                label="Brief Summary"
                active={summaryType === SUMMARY_TYPES.BRIEF}
                onClick={handleSummaryTypeChange}
                disabled={!selectedSession && !selectedCampaign}
              />
              <SummaryButton
                type={SUMMARY_TYPES.MODERATE}
                label="Moderate Summary"
                active={summaryType === SUMMARY_TYPES.MODERATE}
                onClick={handleSummaryTypeChange}
                disabled={!selectedSession && !selectedCampaign}
              />
              <SummaryButton
                type={SUMMARY_TYPES.DETAILED}
                label="Detailed Summary"
                active={summaryType === SUMMARY_TYPES.DETAILED}
                onClick={handleSummaryTypeChange}
                disabled={!selectedSession && !selectedCampaign}
              />
              <SummaryButton
                type={SUMMARY_TYPES.FULL}
                label="Full Transcript"
                active={summaryType === SUMMARY_TYPES.FULL}
                onClick={handleSummaryTypeChange}
                disabled={!selectedSession && !selectedCampaign}
              />
            </div>

            {/* Campaign/Session Content */}
            <div className="bg-white border border-gray-300 rounded-lg p-6 mb-8 relative">
              {selectedSession ? (
                <>
                  <FiEdit2
                    className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 cursor-pointer"
                    onClick={handleEditClick}
                  />
                  {isEditingCampaignName ? (
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded p-2"
                      value={editCampaignName}
                      onChange={(e) => setEditCampaignName(e.target.value)}
                    />
                  ) : (
                    <h2 className="text-black text-xl font-semibold mb-4">
                      {selectedSession
                        ? campaignDetails?.sessions?.find(
                            (s) => s.id === selectedSession
                          )?.title
                        : campaignDetails?.title}
                    </h2>
                  )}
                  <div className="text-gray-600 prose max-w-none" style={{ whiteSpace: 'pre-wrap' }}>
                    {isEditing ? (
                      <textarea
                        className="w-full h-[calc(100vh-200px)] border border-gray-300 rounded p-2"
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                      />
                    ) : (
                      <div dangerouslySetInnerHTML={{ __html: summaryContent }} />
                    )}
                  </div>
                </>
              ) : selectedCampaign ? (
                <>
                  <FiEdit2
                    className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 cursor-pointer"
                    onClick={handleEditClick}
                  />
                  <div className="text-gray-600 prose max-w-none" style={{ whiteSpace: 'pre-wrap' }}>
                    {isEditing ? (
                      <textarea
                        className="w-full h-[calc(100vh-200px)] border border-gray-300 rounded p-2"
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                      />
                    ) : (
                      <div dangerouslySetInnerHTML={{ __html: summaryContent }} />
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-10">
                  <h3 className="text-xl text-gray-500">Please select a campaign or session from the sidebar</h3>
                  <p className="text-gray-400 mt-2">No campaign or session is currently selected</p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-4 mt-8">
              {isEditing ? (
                <>
                  <button
                    onClick={handleSaveEdit}
                    className="px-6 py-2 bg-primary hover:bg-primary-light text-white rounded-md flex-1">
                    Save
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="px-6 py-2 bg-gray-500 hover:bg-gray-400 text-white rounded-md flex-1">
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button 
                    onClick={handleDownloadSummary} 
                    className={`px-6 py-2 ${(selectedSession || (selectedCampaign && campaignSummaries[selectedCampaign])) ? 'bg-primary hover:bg-primary-light' : 'bg-gray-300 cursor-not-allowed'} text-white rounded-md flex-1 flex items-center gap-2`}
                    disabled={!selectedSession && !(selectedCampaign && campaignSummaries[selectedCampaign])}>
                    <VscCloudDownload size={16} />
                    Download
                  </button>
                  <Link
                    to={selectedSession 
                      ? `/qa?transcript_id=${selectedSession}${selectedCampaign ? `&campaign_id=${selectedCampaign}` : ''}` 
                      : selectedCampaign 
                        ? `/qa?campaign_id=${selectedCampaign}` 
                        : '#'
                    }
                    className={`px-6 py-2 ${(selectedSession || selectedCampaign) ? 'bg-sky-900 hover:bg-sky-700' : 'bg-gray-300 cursor-not-allowed'} text-white rounded-md flex-1 text-center`}
                    onClick={e => (!selectedSession && !selectedCampaign) && e.preventDefault()}>
                    Q & A
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>

        <CreateCampaignModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleCreateCampaign}
        />
      </main>
    </>
  );
};

export default Archive;
